import * as turf from '@turf/turf';

// The hike map's trail-network snapper, moved here from the old Quick Measurement
// store module unchanged: v2's "Snap to route" measures along the same network
// (route, alternates and spurs, joined at junctions).

// Route styles the measurement tool is allowed to snap to / trace along.
// Includes the main route plus alternates and spurs so users can measure
// those trails too. 'transport' links (dashed connectors) are excluded.
export const MEASURABLE_ROUTE_STYLES = ['route', 'alt', 'spur'];

export function isMeasurableRouteStyle(routeStyle: string | undefined | null): boolean {
  return MEASURABLE_ROUTE_STYLES.includes(routeStyle || 'route');
}

// ---------------------------------------------------------------------------
// Trail-network routing
//
// To let a measurement cross flexibly between trails (e.g. start on the main
// route and finish on an alt or spur), we treat every measurable trail as part
// of one connected graph. Each trail vertex is a node; consecutive vertices are
// edges weighted by their ground distance. Where trails meet but don't share an
// exact coordinate, we stitch them together within a small tolerance (see
// BRIDGE_TOLERANCE_M) so the network stays connected and a shortest-path search
// can flow from one trail onto another.
// ---------------------------------------------------------------------------

interface TrailGraphNode {
  coord: number[]; // representative [lng, lat, ele?]
  edges: { to: string; w: number }[];
}
export type TrailGraph = Map<string, TrailGraphNode>;

// How close a trail endpoint must come to another trail for us to treat them as
// joined at a junction. Alts/spurs are digitised independently from the main
// route, so their junction point is rarely the exact same coordinate — this
// tolerance lets us stitch them together anyway.
const BRIDGE_TOLERANCE_M = 25;

// How far one day of the main route may end from where the next day starts and
// still be walked as one line. Each day is planned on its own, so the hand-over
// is often tens of metres out (85 m between days 2 and 3 of the Wicklow Way),
// well past BRIDGE_TOLERANCE_M; that split the network and dropped "Snap to
// route" back to a straight line. Kept short of a ferry or bus transfer.
export const DAY_GAP_TOLERANCE_M = 500;

// Quantise a coordinate to ~1m so vertices that are shared (or all-but-identical)
// between trails at a junction collapse onto the same node and become connected.
function trailNodeKey(coord: number[]): string {
  return `${coord[0].toFixed(5)},${coord[1].toFixed(5)}`;
}

function metresBetween(a: number[], b: number[]): number {
  return turf.distance(turf.point([a[0], a[1]]), turf.point([b[0], b[1]]), { units: 'meters' });
}

// ---------------------------------------------------------------------------
// Nearest point on a line
//
// turf.nearestPointOnLine does spherical maths per segment (~20 µs each), and
// the graph's junction bridging asks it about every trail endpoint against
// every other trail: ~7 s on the Wicklow Way and minutes on longer hikes,
// which froze the hike map the moment "Snap to route" went on. Trails are
// metres-to-kilometres apart, so a flat projection centred on the query point
// (cheap-ruler's WGS84 scale factors) is metre-accurate here and ~100× faster.
// ---------------------------------------------------------------------------

/** Metres per degree of longitude (kx) and latitude (ky) at [lat], on the WGS84 ellipsoid. */
export function metresPerDegree(lat: number): { kx: number; ky: number } {
  const flattening = 1 / 298.257223563;
  const e2 = flattening * (2 - flattening);
  const m = (Math.PI / 180) * 6378137;
  const cos = Math.cos((lat * Math.PI) / 180);
  const w2 = 1 / (1 - e2 * (1 - cos * cos));
  const w = Math.sqrt(w2);
  return { kx: m * w * cos, ky: m * w * w2 * (1 - e2) };
}

export interface NearestOnLine {
  /** The nearest point, [lng, lat]. */
  coord: number[];
  /** The segment it lies on: between coords[index] and coords[index + 1]. */
  index: number;
  /** Metres from the query point. */
  dist: number;
}

/** The nearest point to [lngLat] on a polyline (first segment wins a tie), or null for fewer than two vertices. */
export function nearestOnLine(coords: number[][], lngLat: number[], scale = metresPerDegree(lngLat[1])): NearestOnLine | null {
  if (!coords || coords.length < 2) return null;
  const [lng, lat] = lngLat;
  const { kx, ky } = scale;
  let bestD2 = Infinity;
  let bestIndex = 0;
  let bestT = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    const a = coords[i];
    const b = coords[i + 1];
    const ax = (a[0] - lng) * kx;
    const ay = (a[1] - lat) * ky;
    const dx = (b[0] - a[0]) * kx;
    const dy = (b[1] - a[1]) * ky;
    const len2 = dx * dx + dy * dy;
    const t = len2 > 0 ? Math.min(1, Math.max(0, -(ax * dx + ay * dy) / len2)) : 0;
    const px = ax + t * dx;
    const py = ay + t * dy;
    const d2 = px * px + py * py;
    if (d2 < bestD2) {
      bestD2 = d2;
      bestIndex = i;
      bestT = t;
    }
  }
  const a = coords[bestIndex];
  const b = coords[bestIndex + 1];
  return { coord: [a[0] + (b[0] - a[0]) * bestT, a[1] + (b[1] - a[1]) * bestT], index: bestIndex, dist: Math.sqrt(bestD2) };
}

type Box = [number, number, number, number]; // [minLng, minLat, maxLng, maxLat]

function boundingBox(coords: number[][]): Box {
  const box: Box = [Infinity, Infinity, -Infinity, -Infinity];
  coords.forEach((c) => {
    if (c[0] < box[0]) box[0] = c[0];
    if (c[1] < box[1]) box[1] = c[1];
    if (c[0] > box[2]) box[2] = c[0];
    if (c[1] > box[3]) box[3] = c[1];
  });
  return box;
}

// One box per line, worked out once per segments array (a TrailNetwork keeps its array for good).
const boxCache = new WeakMap<number[][][], (Box | null)[]>();
function boxesFor(segments: number[][][]): (Box | null)[] {
  let boxes = boxCache.get(segments);
  if (!boxes) {
    boxes = segments.map((coords) => (coords && coords.length >= 2 ? boundingBox(coords) : null));
    boxCache.set(segments, boxes);
  }
  return boxes;
}

/** Metres from [lngLat] to the nearest edge of [box]: no point on a line inside the box can be closer. */
function metresToBox(box: Box, lngLat: number[], scale: { kx: number; ky: number }): number {
  const dx = Math.max(box[0] - lngLat[0], 0, lngLat[0] - box[2]) * scale.kx;
  const dy = Math.max(box[1] - lngLat[1], 0, lngLat[1] - box[3]) * scale.ky;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * [routeChain] is the main route's lines in walking order (see [routeChain]): each line's nearer
 * end is joined to the next line's, when they're within DAY_GAP_TOLERANCE_M.
 */
export function buildTrailGraph(segments: number[][][], routeChain: number[][][] = []): TrailGraph {
  const graph: TrailGraph = new Map();

  const ensureNode = (coord: number[]): string => {
    const key = trailNodeKey(coord);
    if (!graph.has(key)) {
      graph.set(key, { coord, edges: [] });
    }
    return key;
  };

  const addEdge = (from: string, to: string, w: number) => {
    const node = graph.get(from);
    if (!node || from === to) return;
    if (!node.edges.some((e) => e.to === to)) {
      node.edges.push({ to, w });
    }
  };

  // 1. Edges along each trail (consecutive vertices).
  segments.forEach((coords) => {
    if (!coords || coords.length < 2) return;
    for (let i = 0; i < coords.length - 1; i++) {
      const a = ensureNode(coords[i]);
      const b = ensureNode(coords[i + 1]);
      if (a === b) continue;
      const w = metresBetween(coords[i], coords[i + 1]);
      addEdge(a, b, w);
      addEdge(b, a, w);
    }
  });

  // 2. Bridge trails that meet at a junction but don't share an exact vertex.
  //    Each trail endpoint is projected onto every other trail; if it lands
  //    within tolerance, we stitch the endpoint into that trail's nearest edge
  //    so the network is connected there and routing can cross between trails.
  //    Trails whose bounding box (padded by the tolerance) misses the endpoint
  //    can't be within reach, so they're skipped without walking their vertices.
  const boxes = boxesFor(segments);
  segments.forEach((coords, si) => {
    if (!coords || coords.length < 2) return;
    const endpoints = [coords[0], coords[coords.length - 1]];
    endpoints.forEach((ep) => {
      const scale = metresPerDegree(ep[1]);
      const padLng = BRIDGE_TOLERANCE_M / scale.kx;
      const padLat = BRIDGE_TOLERANCE_M / scale.ky;
      segments.forEach((other, oi) => {
        const box = boxes[oi];
        if (oi === si || !box) return;
        if (ep[0] < box[0] - padLng || ep[0] > box[2] + padLng || ep[1] < box[1] - padLat || ep[1] > box[3] + padLat) return;
        const np = nearestOnLine(other, ep, scale);
        if (!np || np.dist > BRIDGE_TOLERANCE_M) return;

        // Connect the endpoint to the two vertices bracketing the nearest point,
        // effectively inserting it into that trail's edge at the junction.
        const idx = Math.min(np.index, other.length - 2);
        const epKey = ensureNode(ep);
        [other[idx], other[idx + 1]].forEach((bracket) => {
          const bKey = ensureNode(bracket);
          const w = metresBetween(ep, bracket);
          addEdge(epKey, bKey, w);
          addEdge(bKey, epKey, w);
        });
      });
    });
  });

  // 3. Hand each day of the main route on to the next, even where they don't
  //    quite meet: whichever pair of their ends is closest, so a day drawn
  //    backwards still joins up.
  for (let k = 0; k + 1 < routeChain.length; k++) {
    const a = routeChain[k];
    const b = routeChain[k + 1];
    if (!a || !b || a.length < 2 || b.length < 2) continue;
    const pairs = [[a[a.length - 1], b[0]], [a[a.length - 1], b[b.length - 1]], [a[0], b[0]], [a[0], b[b.length - 1]]];
    let best = { gap: Infinity, from: pairs[0][0], to: pairs[0][1] };
    pairs.forEach(([from, to]) => {
      const gap = metresBetween(from, to);
      if (gap < best.gap) best = { gap, from, to };
    });
    if (best.gap > DAY_GAP_TOLERANCE_M) continue;
    const fromKey = ensureNode(best.from);
    const toKey = ensureNode(best.to);
    addEdge(fromKey, toKey, best.gap);
    addEdge(toKey, fromKey, best.gap);
  }

  return graph;
}

// A small binary min-heap keyed by distance. Stale entries are tolerated: the
// caller skips a popped entry whose distance is worse than the best recorded.
class TrailMinHeap {
  private items: { key: string; dist: number }[] = [];

  get size(): number {
    return this.items.length;
  }

  push(key: string, dist: number): void {
    this.items.push({ key, dist });
    let i = this.items.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.items[parent].dist <= this.items[i].dist) break;
      [this.items[parent], this.items[i]] = [this.items[i], this.items[parent]];
      i = parent;
    }
  }

  pop(): { key: string; dist: number } | undefined {
    const top = this.items[0];
    const last = this.items.pop();
    if (this.items.length > 0 && last) {
      this.items[0] = last;
      let i = 0;
      const n = this.items.length;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        let smallest = i;
        const l = 2 * i + 1;
        const r = 2 * i + 2;
        if (l < n && this.items[l].dist < this.items[smallest].dist) smallest = l;
        if (r < n && this.items[r].dist < this.items[smallest].dist) smallest = r;
        if (smallest === i) break;
        [this.items[smallest], this.items[i]] = [this.items[i], this.items[smallest]];
        i = smallest;
      }
    }
    return top;
  }
}

// Snap a point to the nearest measurable trail and return the snapped coordinate
// plus the two trail vertices that bracket it (the graph nodes it sits between).
function snapToNearestSegment(
  segments: number[][][],
  lngLat: [number, number]
): { coord: number[]; brackets: number[][]; line: number; index: number } | null {
  const scale = metresPerDegree(lngLat[1]);
  const boxes = boxesFor(segments);
  // Nearest boxes first, stopping once a box is further off than the best point so far:
  // a long hike's hundreds of lines cost a handful of line walks per snap.
  const order = segments
    .map((_, i) => ({ i, reach: boxes[i] ? metresToBox(boxes[i] as Box, lngLat, scale) : Infinity }))
    .filter((entry) => entry.reach < Infinity)
    .sort((a, b) => a.reach - b.reach || a.i - b.i);
  let best: { coord: number[]; brackets: number[][]; dist: number; i: number; index: number } | null = null;

  for (const { i, reach } of order) {
    if (best && reach > best.dist) break;
    const coords = segments[i];
    const np = nearestOnLine(coords, lngLat, scale);
    // Ties go to the earlier line, as before.
    if (np && (!best || np.dist < best.dist || (np.dist === best.dist && i < best.i))) {
      best = {
        coord: np.coord,
        brackets: [coords[np.index], coords[np.index + 1]],
        dist: np.dist,
        i,
        index: np.index,
      };
    }
  }

  if (!best) return null;
  return { coord: withInterpolatedHeight(best.coord, best.brackets), brackets: best.brackets, line: best.i, index: best.index };
}

/**
 * turf snaps in 2D, so the snapped point has no height; take it from the two vertices either side,
 * so a trail that carries heights can be profiled without asking the elevation service.
 */
export function withInterpolatedHeight(coord: number[], brackets: number[][]): number[] {
  const [a, b] = brackets;
  if (!a || !b || !Number.isFinite(a[2]) || !Number.isFinite(b[2])) return coord;
  const span = metresBetween(a, b);
  const t = span > 0 ? Math.min(1, Math.max(0, metresBetween(a, coord) / span)) : 0;
  return [coord[0], coord[1], a[2] + (b[2] - a[2]) * t];
}

// Find the shortest on-trail path between two arbitrary points by snapping each
// to the trail network and running multi-source Dijkstra between the vertices
// that bracket them. Returns the path coordinates (snapped endpoints included),
// or null when the two points are on disconnected parts of the network.
export function shortestPathBetween(
  graph: TrailGraph,
  segments: number[][][],
  startLngLat: [number, number],
  endLngLat: [number, number]
): number[][] | null {
  if (segments.length === 0) return null;

  const startSnap = snapToNearestSegment(segments, startLngLat);
  const endSnap = snapToNearestSegment(segments, endLngLat);
  if (!startSnap || !endSnap) return null;

  const sources = startSnap.brackets
    .map((c) => ({ key: trailNodeKey(c), w: metresBetween(startSnap.coord, c) }))
    .filter((e) => graph.has(e.key));
  const targets = endSnap.brackets
    .map((c) => ({ key: trailNodeKey(c), w: metresBetween(endSnap.coord, c) }))
    .filter((e) => graph.has(e.key));

  if (sources.length === 0 || targets.length === 0) return null;

  const dist = new Map<string, number>();
  const prev = new Map<string, string | null>();
  const heap = new TrailMinHeap();

  sources.forEach(({ key, w }) => {
    if (!dist.has(key) || w < (dist.get(key) as number)) {
      dist.set(key, w);
      prev.set(key, null);
      heap.push(key, w);
    }
  });

  const targetKeys = new Set(targets.map((t) => t.key));
  let settled = 0;

  while (heap.size > 0) {
    const top = heap.pop();
    if (!top) break;
    const { key, dist: d } = top;
    if (d > (dist.get(key) ?? Infinity)) continue;
    if (targetKeys.has(key)) {
      settled++;
      if (settled >= targetKeys.size) break;
    }
    const node = graph.get(key);
    if (!node) continue;
    for (const edge of node.edges) {
      const nd = d + edge.w;
      if (nd < (dist.get(edge.to) ?? Infinity)) {
        dist.set(edge.to, nd);
        prev.set(edge.to, key);
        heap.push(edge.to, nd);
      }
    }
  }

  // Choose the target bracket that minimises the full distance to the snapped end.
  let bestTarget: string | null = null;
  let bestTotal = Infinity;
  targets.forEach(({ key, w }) => {
    const dv = dist.get(key);
    if (dv !== undefined && dv + w < bestTotal) {
      bestTotal = dv + w;
      bestTarget = key;
    }
  });
  if (bestTarget === null) return null;

  // Walk the predecessor chain back to a source vertex.
  const reversed: string[] = [];
  const seen = new Set<string>();
  let cur: string | null = bestTarget;
  while (cur !== null && !seen.has(cur)) {
    seen.add(cur);
    reversed.push(cur);
    cur = prev.get(cur) ?? null;
  }
  reversed.reverse();

  const pathCoords = reversed.map((k) => (graph.get(k) as TrailGraphNode).coord);
  const result = [startSnap.coord, ...pathCoords, endSnap.coord];

  // Drop consecutive duplicate coordinates introduced by the snapped bookends.
  const deduped: number[][] = [];
  for (const c of result) {
    const last = deduped[deduped.length - 1];
    if (!last || last[0] !== c[0] || last[1] !== c[1]) deduped.push(c);
  }

  return deduped.length >= 2 ? deduped : null;
}

interface MeasurableLine {
  coords: number[][];
  feature: any;
  /** The feature's place in the collection. */
  order: number;
}

const isMainRoute = (line: MeasurableLine) => (line.feature.properties?.routeStyle || 'route') === 'route';

/** Every measurable line with the feature it came from, in collection order. */
function measurableLines(geoJson: any): MeasurableLine[] {
  const out: MeasurableLine[] = [];
  (geoJson?.features || []).forEach((feature: any, order: number) => {
    const geometry = feature?.geometry;
    if (!geometry || !isMeasurableRouteStyle(feature.properties?.routeStyle)) return;
    const lines = geometry.type === 'MultiLineString' ? geometry.coordinates
      : geometry.type === 'LineString' ? [geometry.coordinates] : [];
    lines.forEach((coords: number[][]) => {
      if (coords && coords.length >= 2) out.push({ coords, feature, order });
    });
  });
  return out;
}

/** Every measurable line of a hike or recording's GeoJSON (LineString or MultiLineString features). */
export function measurableSegments(geoJson: any): number[][][] {
  return measurableLines(geoJson).map((line) => line.coords);
}

/** A feature's hike day, for ordering; features without one keep their place after the numbered days. */
function dayOf(feature: any): number {
  const day = Number(feature?.properties?.hikeDayNumber);
  return Number.isFinite(day) ? day : Number.MAX_SAFE_INTEGER;
}

/** Indexes of the main route's lines in walking order: by hike day, then as listed. */
function walkingOrder(lines: MeasurableLine[]): number[] {
  return lines.map((_, i) => i).filter((i) => isMainRoute(lines[i]))
    .sort((a, b) => dayOf(lines[a].feature) - dayOf(lines[b].feature) || lines[a].order - lines[b].order || a - b);
}

/**
 * The main route's lines (not alts, spurs or transport links) in walking order: by hike day, then
 * as listed, each multi-line feature's parts in turn.
 */
export function routeChain(geoJson: any): number[][][] {
  const lines = measurableLines(geoJson);
  return walkingOrder(lines).map((i) => lines[i].coords);
}

/**
 * How far along the hike each line's vertices are, counted as the hike map's distance markers
 * count: the main route from the hike's start, on through the days (the hand-over gaps between
 * days aren't walked, so they aren't counted); each alt or spur from its own start, across its
 * planned trail's lines.
 */
function odometerFor(lines: MeasurableLine[]): number[][] {
  const along = lines.map(({ coords }) => {
    const out = [0];
    for (let i = 1; i < coords.length; i++) out.push(out[i - 1] + metresBetween(coords[i - 1], coords[i]));
    return out;
  });
  const groups: number[][] = [walkingOrder(lines)];
  const branches = new Map<string, number[]>();
  lines.forEach((line, i) => {
    if (isMainRoute(line)) return;
    const trailId = line.feature.properties?.ptv2PlannedTrailId;
    const key = trailId != null ? `trail:${trailId}` : `feature:${line.order}`;
    if (!branches.has(key)) branches.set(key, []);
    (branches.get(key) as number[]).push(i);
  });
  groups.push(...branches.values());
  groups.forEach((group) => {
    let offset = 0;
    group.forEach((i) => {
      const length = along[i][along[i].length - 1];
      along[i] = along[i].map((metres) => metres + offset);
      offset += length;
    });
  });
  return along;
}

/**
 * One hike's network, built once: snap a point onto it, or trace the on-trail path
 * between two points. The graph build is the expensive part, so a drag reuses it.
 */
export class TrailNetwork {
  readonly segments: number[][][];
  private readonly lines: MeasurableLine[];
  private readonly chain: number[][][];
  private graph: TrailGraph | null = null;
  private odometer: number[][] | null = null;

  constructor(geoJson: any) {
    this.lines = measurableLines(geoJson);
    this.segments = this.lines.map((line) => line.coords);
    this.chain = walkingOrder(this.lines).map((i) => this.lines[i].coords);
  }

  get isEmpty(): boolean {
    return this.segments.length === 0;
  }

  /** The nearest point on any measurable line, as [lng, lat, ele?]. */
  snap(lngLat: [number, number]): number[] | null {
    return snapToNearestSegment(this.segments, lngLat)?.coord ?? null;
  }

  /**
   * The nearest point on any measurable line, and how far along the hike it is in metres, as the
   * hike map's distance markers count (see [odometerFor]).
   */
  locate(lngLat: [number, number]): { coord: number[]; along: number } | null {
    const found = snapToNearestSegment(this.segments, lngLat);
    if (!found) return null;
    if (!this.odometer) this.odometer = odometerFor(this.lines);
    const from = this.segments[found.line][found.index];
    return { coord: found.coord, along: this.odometer[found.line][found.index] + metresBetween(from, found.coord) };
  }

  /** The on-trail path between two points (snapped ends included), or null when they're on disconnected trails. */
  path(start: [number, number], end: [number, number]): number[][] | null {
    if (this.isEmpty) return null;
    if (!this.graph) this.graph = buildTrailGraph(this.segments, this.chain);
    return shortestPathBetween(this.graph, this.segments, start, end);
  }
}
